/**
 * InfinitSales - Register Page
 * Modern design with inline styles and theme support
 * 
 * UPDATED: 
 * - Removed infinitagents_api_key field (now automatic)
 * - Redirects to /onboarding after registration
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, Check, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import authService from '../../services/global/authService';

// Confirmação de e-mail no cadastro (auth-config.email_verification_required,
// 2026-10-14): o submit manda o código, a tela pede os 6 dígitos e só então
// cria a conta. Desligada, o cadastro é o de sempre.
const RESEND_COOLDOWN_SECONDS = 60;

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail ? detail : fallback;
};

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { colors, theme } = useTheme();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    // 🚫 REMOVED: infinitagentsApiKey - now generated automatically
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'code'
  const [verificationToken, setVerificationToken] = useState('');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [info, setInfo] = useState('');

  useEffect(() => {
    let alive = true;
    authService.getAuthConfig().then((cfg) => {
      if (alive) setVerificationRequired(!!cfg?.email_verification_required);
    });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const sendVerificationCode = async () => {
    const response = await authService.sendEmailVerification(formData.email.trim(), formData.name.trim() || null);
    setVerificationToken(response.verification_token);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    setError('');
    try {
      await sendVerificationCode();
      setCode('');
      setInfo('Enviamos um código novo. Use sempre o último que chegar.');
    } catch (err) {
      setError(errorText(err, 'Não foi possível reenviar o código agora.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep('form');
    setCode('');
    setInfo('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    // Confirmação de e-mail: primeiro envia o código e pede os 6 dígitos.
    if (verificationRequired && step === 'form') {
      try {
        await sendVerificationCode();
        setCode('');
        setInfo(`Enviamos um código de 6 dígitos para ${formData.email.trim()}. Confira também o spam.`);
        setStep('code');
      } catch (err) {
        setError(errorText(err, 'Não foi possível enviar o código agora. Tente de novo em instantes.'));
      } finally {
        setLoading(false);
      }
      return;
    }

    if (verificationRequired && code.length !== 6) {
      setError('Digite os 6 dígitos do código.');
      setLoading(false);
      return;
    }

    try {
      // 🆕 UPDATED: No longer passing infinitagentsApiKey (now automatic)
      await register(
        formData.email, 
        formData.password, 
        formData.name,
        verificationRequired ? { token: verificationToken, code } : null
      );
      // Navigate to onboarding
      navigate('/onboarding');
    } catch (err) {
      setError(errorText(err, 'Erro ao criar conta. Tente novamente.'));
    } finally {
      setLoading(false);
    }
  };

  const checklistItems = [
    'Escolha entre WhatsApp Pessoal ou Business',
    'Inbox completo estilo WhatsApp Web',
    'Sistema de labels e organização',
    'Agente IA para respostas automáticas',
    'Follow-ups e reengajamento',
    'Suporte técnico dedicado',
  ];

  const styles = {
    container: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: colors.bgSecondary,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    },
    branding: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '48px',
      backgroundColor: colors.bgPrimary,
      borderRight: `1px solid ${colors.borderColor}`,
      position: 'relative',
      overflow: 'hidden',
    },
    brandingBg: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 70% 80%, ${colors.accentPrimary}15 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
    brandingContent: {
      position: 'relative',
      zIndex: 1,
      maxWidth: '480px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '48px',
      cursor: 'pointer',
    },
    logoIcon: {
      width: '48px',
      height: '48px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${colors.borderColor}`,
      overflow: 'hidden',
    },
    logoText: {
      fontSize: '24px',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    title: {
      fontSize: '42px',
      fontWeight: 700,
      lineHeight: 1.2,
      marginBottom: '24px',
      color: colors.textPrimary,
    },
    titleGradient: {
      background: `linear-gradient(135deg, ${colors.accentPrimary} 0%, #34d399 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    description: {
      fontSize: '18px',
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '48px',
    },
    checklist: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    checkItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      fontSize: '15px',
      color: colors.textPrimary,
    },
    checkIcon: {
      width: '24px',
      height: '24px',
      backgroundColor: `${colors.accentPrimary}20`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
      flexShrink: 0,
    },
    formContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
    },
    formWrapper: {
      width: '100%',
      maxWidth: '400px',
    },
    formHeader: {
      marginBottom: '32px',
      textAlign: 'center',
    },
    formTitle: {
      fontSize: '28px',
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    formSubtitle: {
      fontSize: '15px',
      color: colors.textSecondary,
    },
    error: {
      padding: '12px 16px',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#ef4444',
      fontSize: '14px',
      marginBottom: '24px',
      textAlign: 'center',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '18px',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '14px',
      color: colors.textTertiary,
      pointerEvents: 'none',
      transition: 'color 0.2s',
    },
    input: {
      width: '100%',
      padding: '14px 14px 14px 46px',
      backgroundColor: colors.bgPrimary,
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
      transition: 'border-color 0.2s, box-shadow 0.2s',
    },
    inputFocused: {
      borderColor: colors.accentPrimary,
      boxShadow: `0 0 0 3px ${colors.accentPrimary}20`,
    },
    inputAction: {
      position: 'absolute',
      right: '12px',
      padding: '6px',
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    passwordRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '12px',
    },
    submitBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 24px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '15px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background-color 0.2s, transform 0.1s',
      marginTop: '8px',
    },
    submitBtnDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
    footer: {
      marginTop: '32px',
      textAlign: 'center',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    link: {
      color: colors.accentPrimary,
      textDecoration: 'none',
      fontWeight: 500,
    },
    info: {
      padding: '12px 16px',
      backgroundColor: `${colors.accentPrimary}12`,
      border: `1px solid ${colors.accentPrimary}40`,
      borderRadius: '8px',
      color: colors.textPrimary,
      fontSize: '14px',
      lineHeight: 1.5,
      marginBottom: '24px',
      textAlign: 'center',
    },
    codeInput: {
      fontSize: '22px',
      fontWeight: 600,
      letterSpacing: '0.5em',
      fontVariantNumeric: 'tabular-nums',
    },
    textBtn: {
      background: 'transparent',
      border: 'none',
      padding: 0,
      color: colors.accentPrimary,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
  };

  // Add responsive styles
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 900;

  if (isMobile) {
    styles.branding.display = 'none';
    styles.formContainer.padding = '24px';
    styles.passwordRow.gridTemplateColumns = '1fr';
  }

  return (
    <div style={styles.container}>
      {/* Left Side - Branding */}
      <div style={styles.branding}>
        <div style={styles.brandingBg} />
        <div style={styles.brandingContent}>
          <Link to="/home" style={{ textDecoration: 'none' }}>
            <div style={styles.logo}>
              <div style={styles.logoIcon}>
                <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="InfinitSales" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
              </div>
              <span style={styles.logoText}>InfinitSales</span>
            </div>
          </Link>

          <h1 style={styles.title}>
            Comece agora
            <br />
            <span style={styles.titleGradient}>em poucos minutos</span>
          </h1>

          <p style={styles.description}>
            Crie sua conta gratuita e conecte seu WhatsApp.
            Configure seu agente de vendas em minutos.
          </p>

          <div style={styles.checklist}>
            {checklistItems.map((item, index) => (
              <div key={index} style={styles.checkItem}>
                <div style={styles.checkIcon}>
                  <Check size={14} />
                </div>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div style={styles.formContainer}>
        <div style={styles.formWrapper}>
          {/* Mobile Logo */}
          {isMobile && (
            <Link to="/home" style={{ textDecoration: 'none' }}>
              <div style={{ ...styles.logo, justifyContent: 'center', marginBottom: '32px' }}>
                <div style={styles.logoIcon}>
                  <img src={process.env.PUBLIC_URL + '/logo.jpeg'} alt="InfinitSales" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
                </div>
                <span style={styles.logoText}>InfinitSales</span>
              </div>
            </Link>
          )}

          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>{step === 'code' ? 'Confirme seu e-mail' : 'Criar conta'}</h2>
            <p style={styles.formSubtitle}>
              {step === 'code' ? 'Digite o código que enviamos para concluir o cadastro' : 'Preencha os dados para começar'}
            </p>
          </div>

          {error ? <div style={styles.error}>{error}</div> : (step === 'code' && info && <div style={styles.info}>{info}</div>)}

          <form onSubmit={handleSubmit} style={styles.form}>
            {step === 'code' ? (
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="code">Código de 6 dígitos</label>
                <div style={styles.inputWrapper}>
                  <KeyRound
                    size={18}
                    style={{
                      ...styles.inputIcon,
                      color: focusedField === 'code' ? colors.accentPrimary : colors.textTertiary
                    }}
                  />
                  <input
                    type="text"
                    id="code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="000000"
                    maxLength={6}
                    value={code}
                    onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                    onFocus={() => setFocusedField('code')}
                    onBlur={() => setFocusedField(null)}
                    required
                    autoFocus
                    style={{
                      ...styles.input,
                      ...styles.codeInput,
                      ...(focusedField === 'code' ? styles.inputFocused : {}),
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button type="button" style={styles.textBtn} onClick={handleBackToForm}>
                    Corrigir dados
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.textBtn, ...(cooldown > 0 ? { color: colors.textTertiary, cursor: 'default' } : {}) }}
                    onClick={handleResend}
                    disabled={cooldown > 0 || loading}
                  >
                    {cooldown > 0 ? `Reenviar código em ${cooldown}s` : 'Reenviar código'}
                  </button>
                </div>
              </div>
            ) : (
            <>
            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="name">Nome</label>
              <div style={styles.inputWrapper}>
                <User 
                  size={18} 
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'name' ? colors.accentPrimary : colors.textTertiary
                  }} 
                />
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="name"
                  style={{
                    ...styles.input,
                    ...(focusedField === 'name' ? styles.inputFocused : {}),
                  }}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="email">E-mail</label>
              <div style={styles.inputWrapper}>
                <Mail 
                  size={18} 
                  style={{
                    ...styles.inputIcon,
                    color: focusedField === 'email' ? colors.accentPrimary : colors.textTertiary
                  }} 
                />
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="email"
                  style={{
                    ...styles.input,
                    ...(focusedField === 'email' ? styles.inputFocused : {}),
                  }}
                />
              </div>
            </div>

            {/* 🚫 REMOVED: InfinitAgents API Key field - now automatic */}

            <div style={isMobile ? {} : styles.passwordRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="password">Senha</label>
                <div style={styles.inputWrapper}>
                  <Lock 
                    size={18} 
                    style={{
                      ...styles.inputIcon,
                      color: focusedField === 'password' ? colors.accentPrimary : colors.textTertiary
                    }}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    style={{
                      ...styles.input,
                      paddingRight: '46px',
                      ...(focusedField === 'password' ? styles.inputFocused : {}),
                    }}
                  />
                  <button
                    type="button"
                    style={styles.inputAction}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="confirmPassword">Confirmar</label>
                <div style={styles.inputWrapper}>
                  <Lock 
                    size={18} 
                    style={{
                      ...styles.inputIcon,
                      color: focusedField === 'confirmPassword' ? colors.accentPrimary : colors.textTertiary
                    }}
                  />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => setFocusedField(null)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    style={{
                      ...styles.input,
                      ...(focusedField === 'confirmPassword' ? styles.inputFocused : {}),
                    }}
                  />
                </div>
              </div>
            </div>
            </>
            )}

            <button 
              type="submit" 
              style={{
                ...styles.submitBtn,
                ...(loading ? styles.submitBtnDisabled : {}),
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  {verificationRequired && step === 'form' ? 'Enviando código...' : 'Criando conta...'}
                </>
              ) : (
                <>
                  {verificationRequired && step === 'form' ? 'Continuar' : 'Criar conta'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div style={styles.footer}>
            <p>
              Já tem uma conta?{' '}
              <Link to="/login" style={styles.link}>
                Fazer login
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        input::placeholder {
          color: ${colors.textTertiary};
        }
        @media (max-width: 900px) {
          .auth-branding {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Register;